import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatService } from '../services/chat.service'
import type { StartConversationPayload } from '../types/chat.types'

export const chatKeys = {
  all: ['chat'] as const,
  conversations: () => [...chatKeys.all, 'conversations'] as const,
  conversation: (id: number) => [...chatKeys.all, 'conversation', id] as const,
  messages: (conversationId: number) => [...chatKeys.all, 'messages', conversationId] as const,
}

export function useConversations() {
  return useQuery({
    queryKey: chatKeys.conversations(),
    queryFn: async () => {
      const res = await chatService.getConversations()
      return res.data ?? []
    },
    refetchInterval: 5000, // Poll every 5s for new messages
  })
}

export function useConversation(id: number | null) {
  return useQuery({
    queryKey: chatKeys.conversation(id!),
    queryFn: async () => {
      const res = await chatService.getConversation(id!)
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useMessages(conversationId: number | null) {
  return useQuery({
    queryKey: chatKeys.messages(conversationId!),
    queryFn: async () => {
      const res = await chatService.getMessages(conversationId!)
      return res.data ?? []
    },
    enabled: Boolean(conversationId),
    refetchInterval: 3000, // Poll every 3s for chat window active
  })
}

export function useStartConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: StartConversationPayload) => chatService.startConversation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() })
    },
  })
}

export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: number; content: string }) =>
      chatService.sendMessage(conversationId, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(variables.conversationId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() })
    },
  })
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) => chatService.markAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() })
    },
  })
}

export function useMarkDealDone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) => chatService.markDealDone(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversation(conversationId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(conversationId) })
    },
  })
}

